import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Constants from "../constants";
import {
    CREATE_SESSION,
    LOAD_CONFIG,
    RESET_FOR_MODEL_SELECTOR,
    SET_IS_LOADING,
    START_IN_PRESET_SESSION,
} from "../store/actions/sessionActions";
import { getHasSession } from "../store/sagas/selectors";
import { goToModelSelector, isStartInPresetTrue } from "../utilities/navigationUtils";

/**
 * Dispatches CREATE_SESSION or LOAD_CONFIG_INTO_EXISTING_SESSION when the configurator or summary route is shown with config_string in the URL.
 * When navigating to model selector, clears configurator state (RESET_FOR_MODEL_SELECTOR) and keeps session.
 */
export function SessionInitFromUrl() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const hasSession = useSelector(getHasSession);
    // Records the `location.search` value for which the default-preset override last
    // dispatched `START_IN_PRESET_SESSION`. Used to (a) suppress duplicate dispatch in
    // StrictMode and (b) skip the post-CREATE_SESSION re-fire's stale `LOAD_CONFIG` for
    // the same URL.
    const defaultPresetHandledForSearchRef = useRef<string | null>(null);

    useEffect(() => {
        // Normalize pathname for robust matching (RR v6 usually gives leading slash; allow trailing slash).
        const pathname = "/" + (location.pathname || "").replace(/^\/+/, "").replace(/\/+$/, "") || "/";
        // Strip trailing slash from the constants so the appended \/? matches both forms.
        const escapeRoute = (route: string) => route.replace(/\/$/, "").replace(/\//g, "\\/");
        const configuratorRegex = new RegExp(`^${escapeRoute(Constants.configuratorUrlPath)}\\/?$`);
        const modelSelectorRegex = new RegExp(`^${escapeRoute(Constants.modelSelectorUrlPath)}\\/?$`);
        const summaryRegex = new RegExp(`^${escapeRoute(Constants.summaryUrlPath)}\\/?$`);
        const isConfiguratorRoute = pathname === "/" || configuratorRegex.test(pathname);
        const isModelSelectorRoute = modelSelectorRegex.test(pathname);
        const isSummaryRoute = summaryRegex.test(pathname);

        const searchParams = new URLSearchParams(location.search);
        const startInPreset = isStartInPresetTrue(searchParams);

        // Default-preset override: a fresh load (no existing session) on any SPA route with
        // start_in_preset=true should land in the default preset, regardless of which
        // route the URL opened on or whether a stale `config_string` is in the URL
        // (e.g. one appended by buildUrlStateSearch during a prior customer's interaction).
        // The configurator route triggers the saga directly; selector/summary bounce to
        // the default-preset entry URL on the configurator route and re-enter this effect there.
        // The ref-check also suppresses StrictMode-driven double-dispatch in dev (effect
        // runs twice on mount with `hasSession=false` both times before the saga resolves).
        if (!hasSession && startInPreset && defaultPresetHandledForSearchRef.current !== location.search) {
            if (isConfiguratorRoute) {
                defaultPresetHandledForSearchRef.current = location.search;
                dispatch({ type: START_IN_PRESET_SESSION });
            } else if (isModelSelectorRoute || isSummaryRoute) {
                navigate(`${Constants.configuratorUrlPath}${location.search}`, { replace: true });
            }
            return;
        }

        if (isModelSelectorRoute) {
            dispatch({ type: SET_IS_LOADING, isLoading: false });
            dispatch({ type: RESET_FOR_MODEL_SELECTOR });
            return;
        }

        // The default-preset override already dispatched START_IN_PRESET_SESSION for this
        // exact URL. The saga is loading the preset — don't dispatch LOAD_CONFIG for the
        // URL's stale config_string on the post-CREATE_SESSION re-fire and clobber the preset.
        if (startInPreset && defaultPresetHandledForSearchRef.current === location.search) {
            return;
        }

        const initialConfig = searchParams.get("config_string") || searchParams.get("encoded_config");
        const configEncoding = searchParams.get("enc_type");
        const surfaceProductArticleNo = searchParams.get("surface_product");

        if (isSummaryRoute) {
            if (initialConfig) {
                dispatch({ type: SET_IS_LOADING, isLoading: true });
                if (hasSession) {
                    dispatch({
                        type: LOAD_CONFIG,
                        initialConfig,
                        configEncoding: configEncoding || undefined,
                        surfaceProductArticleNo: surfaceProductArticleNo || undefined,
                    });
                } else {
                    dispatch({
                        type: CREATE_SESSION,
                        initialConfig,
                        configEncoding,
                        surfaceProductArticleNo,
                    });
                }
            } else if (!hasSession) {
                goToModelSelector(undefined, location.search);
            } else {
                dispatch({ type: SET_IS_LOADING, isLoading: false });
            }
            return;
        }

        if (initialConfig && isConfiguratorRoute) {
            dispatch({ type: SET_IS_LOADING, isLoading: true });
            if (hasSession) {
                dispatch({
                    type: LOAD_CONFIG,
                    initialConfig,
                    configEncoding: configEncoding || undefined,
                    surfaceProductArticleNo: surfaceProductArticleNo || undefined,
                });
            } else {
                dispatch({
                    type: CREATE_SESSION,
                    initialConfig,
                    configEncoding,
                    surfaceProductArticleNo,
                });
            }
        } else if (isConfiguratorRoute && !initialConfig && !startInPreset) {
            dispatch({ type: SET_IS_LOADING, isLoading: false });
            goToModelSelector(undefined, location.search);
        }
    }, [location.pathname, location.search, dispatch, hasSession, navigate]);

    return null;
}
